import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Info, Loader2, PencilLine, RefreshCw } from "lucide-react";
import { CopropertyDto, CopropertyEligibilityStatus, BuildingStanding, TerrainEstimationDto, UpdateCopropertyCommand } from "api/coproperty";
import { Dropdown, DropdownItem } from "components/forms/Dropdown";
import { InputText } from "components/forms/InputText";
import { InputWithUnit } from "components/forms/fragments/InputWithUnit";
import {
  CompactBooleanRow,
  EligibilityVerdict,
  EligibilityVerdictBanner,
  NatureChip,
  parseDecimal,
  DEFAULT_BUILDING_STANDING,
  BUILDING_STANDING_OPTIONS,
} from "./CopropertyFieldControls";
import { CopropertySection } from "./CopropertyStepLayout";

const BUILDING_STANDING_ITEMS: DropdownItem<number>[] = BUILDING_STANDING_OPTIONS.map((o) => ({ key: o.value, label: o.label }));

interface CopropertyFormProps {
  coproperty: CopropertyDto;
  /** Verdict de la sous-étape rendu par le dernier enregistrement (null tant qu'il n'y en a pas eu). */
  verdict: EligibilityVerdict | null;
  /**
   * Expose au bouton d'étape de la page la commande à enregistrer, ou null si la saisie
   * est invalide (le formulaire affiche alors l'erreur). La page fait l'appel et tranche
   * sur le verdict renvoyé.
   */
  registerBuildCommand: (build: () => UpdateCopropertyCommand | null) => void;
  /** Saisie complète et valide (champs du verdict renseignés, surfaces cohérentes) : active le bouton d'étape. */
  onCanSubmitChange: (canSubmit: boolean) => void;
  /**
   * Estimation du terrain depuis le point de la copropriété. Appelée à l'ouverture quand
   * l'espace vert ou l'altitude manquent, et sur « Recalculer » ; null si indisponible.
   */
  onEstimateTerrain?: () => Promise<TerrainEstimationDto | null>;
}

const toInput = (value: number | null | undefined) => (value != null ? String(value) : "");
const formatArea = (value: number) => `${Math.round(value).toLocaleString("fr-FR")} m²`;

/** Au-delà de ce seuil, la question « à plus de 1 500 m d'altitude » se pré-remplit à oui. */
const ALTITUDE_THRESHOLD_M = 1500;

/**
 * Fiche de la copropriété (attributs structurels — spec produit du 2026-08-31), en
 * sections de la carte principale : surfaces, caractéristiques, natures. L'identité et le
 * verdict vivent dans la carte de gauche de la page. Pas d'enregistrement automatique :
 * la fiche est enregistrée en un seul appel au clic sur le bouton d'étape, dont la
 * réponse porte le verdict. Une garde navigateur prévient la perte de saisie si l'onglet
 * est fermé ou rechargé avec des modifications. L'espace vert et l'altitude sont
 * pré-remplis depuis l'estimation du terrain (IGN, Google) quand ils manquent, et
 * restent ajustables.
 */
export const CopropertyForm: FC<CopropertyFormProps> = ({
  coproperty,
  verdict,
  registerBuildCommand,
  onCanSubmitChange,
  onEstimateTerrain,
}) => {
  const [name, setName] = useState(coproperty.name ?? "");
  const [builtMoreThan10, setBuiltMoreThan10] = useState<boolean | null>(coproperty.builtMoreThan10YearsAgo ?? null);
  const [area, setArea] = useState(toInput(coproperty.area));
  const [occupiedLess50, setOccupiedLess50] = useState<boolean | null>(coproperty.occupiedLessThan50Percent ?? null);
  const [listedOrRegistered, setListedOrRegistered] = useState<boolean | null>(coproperty.listedOrRegistered ?? null);
  const [buildingStanding, setBuildingStanding] = useState<BuildingStanding>(coproperty.buildingStanding ?? DEFAULT_BUILDING_STANDING);

  const [greenSpaceArea, setGreenSpaceArea] = useState(toInput(coproperty.greenSpaceArea));
  const [above1500m, setAbove1500m] = useState<boolean | null>(coproperty.above1500mAltitude ?? null);
  const [altitudeMeters, setAltitudeMeters] = useState<number | null>(coproperty.altitudeMeters ?? null);
  /** Dernière estimation du terrain reçue (pour les notes sous les champs). */
  const [estimation, setEstimation] = useState<TerrainEstimationDto | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [fireStationWithin3Km, setFireStationWithin3Km] = useState<boolean | null>(
    coproperty.fireStationWithin3Km ?? null
  );

  const [natureHousing, setNatureHousing] = useState(coproperty.natureHousing ?? false);
  const [natureOffices, setNatureOffices] = useState(coproperty.natureOffices ?? false);
  const [natureShops, setNatureShops] = useState(coproperty.natureShops ?? false);
  const [natureParking, setNatureParking] = useState(coproperty.natureParking ?? false);
  const [parkingArea, setParkingArea] = useState(toInput(coproperty.commercialParkingArea));
  const [shopsArea, setShopsArea] = useState(toInput(coproperty.shopsArea));
  const [shopType, setShopType] = useState(coproperty.shopType ?? "");
  const [officesArea, setOfficesArea] = useState(toInput(coproperty.officesArea));

  // La copro « vacante » (occupation < 50 %) oriente vers le produit « MRI inoccupé ».
  const isVacant = occupiedLess50 === true;

  const parsedArea = useMemo(() => parseDecimal(area), [area]);
  const parsedGreenSpaceArea = useMemo(() => parseDecimal(greenSpaceArea), [greenSpaceArea]);
  const parsedParkingArea = useMemo(() => parseDecimal(parkingArea), [parkingArea]);
  const parsedShopsArea = useMemo(() => parseDecimal(shopsArea), [shopsArea]);
  const parsedOfficesArea = useMemo(() => parseDecimal(officesArea), [officesArea]);

  // Validations de saisie de la spec (miroir du back) : chaque surface par nature < surface totale.
  const exceedsTotal = (value: number | null, enabled: boolean) =>
    enabled && value != null && parsedArea != null && parsedArea > 0 && value >= parsedArea;
  const areaErrors = {
    parking: exceedsTotal(parsedParkingArea, natureParking),
    shops: exceedsTotal(parsedShopsArea, natureShops),
    offices: exceedsTotal(parsedOfficesArea, natureOffices),
  };
  const hasAreaError = areaErrors.parking || areaErrors.shops || areaErrors.offices;
  const areaErrorHint = "Doit être inférieure à la surface totale de la copropriété.";

  const command = useMemo<UpdateCopropertyCommand>(
    () => ({
      name: name.trim() || undefined,
      registrationNumber: coproperty.registrationNumber ?? undefined,
      builtMoreThan10YearsAgo: builtMoreThan10,
      area: parsedArea,
      greenSpaceArea: parsedGreenSpaceArea,
      above1500mAltitude: above1500m,
      altitudeMeters,
      fireStationWithin3Km: above1500m ? fireStationWithin3Km : null,
      occupiedLessThan50Percent: occupiedLess50,
      listedOrRegistered,
      buildingStanding,
      natureHousing,
      natureOffices,
      natureShops,
      natureParking,
      commercialParkingArea: natureParking ? parsedParkingArea : null,
      shopsArea: natureShops ? parsedShopsArea : null,
      shopType: natureShops ? shopType.trim() || null : null,
      officesArea: natureOffices ? parsedOfficesArea : null,
    }),
    [
      coproperty.registrationNumber,
      name,
      builtMoreThan10,
      parsedArea,
      parsedGreenSpaceArea,
      above1500m,
      altitudeMeters,
      fireStationWithin3Km,
      occupiedLess50,
      listedOrRegistered,
      buildingStanding,
      natureHousing,
      natureOffices,
      natureShops,
      natureParking,
      parsedParkingArea,
      parsedShopsArea,
      shopType,
      parsedOfficesArea,
    ]
  );

  /**
   * Estime le terrain et remplit les champs vides (ou tous, si `overwrite`) : espace vert
   * = parcelle − bâti, altitude et sa question à 1 500 m. Le courtier ajuste ensuite.
   */
  const applyEstimation = useCallback(
    async (overwrite: boolean) => {
      if (!onEstimateTerrain) return;
      setIsEstimating(true);
      try {
        const result = await onEstimateTerrain();
        setEstimation(result);
        if (!result) return;

        if (result.greenSpaceArea != null && (overwrite || !greenSpaceArea.trim())) {
          setGreenSpaceArea(String(Math.round(result.greenSpaceArea)));
        }
        if (result.altitudeMeters != null && (overwrite || above1500m == null)) {
          setAltitudeMeters(result.altitudeMeters);
          setAbove1500m(result.altitudeMeters >= ALTITUDE_THRESHOLD_M);
        }
      } finally {
        setIsEstimating(false);
      }
    },
    [onEstimateTerrain, greenSpaceArea, above1500m]
  );

  // À l'ouverture, seulement si quelque chose manque : la saisie du courtier prime.
  const hasEstimatedOnMount = useRef(false);
  useEffect(() => {
    if (hasEstimatedOnMount.current) return;
    hasEstimatedOnMount.current = true;
    if (!greenSpaceArea.trim() || above1500m == null) {
      void applyEstimation(false);
    }
  }, [applyEstimation, greenSpaceArea, above1500m]);

  const greenSpaceHint = useMemo(() => {
    if (!estimation?.parcelArea) return undefined;
    const parts = [`Estimation IGN : parcelle ${formatArea(estimation.parcelArea)}`];
    if (estimation.buildingsFootprint != null) parts.push(`− bâti ${formatArea(estimation.buildingsFootprint)}`);
    return `${parts.join(" ")} · parcelle du point uniquement, ajustable`;
  }, [estimation]);

  const altitudeHint = useMemo(() => {
    if (altitudeMeters == null) return undefined;
    return `Altitude estimée : ${Math.round(altitudeMeters)} m`;
  }, [altitudeMeters]);

  // Modifications non enregistrées : la commande courante diffère de celle du montage.
  const [initialCommand] = useState(() => JSON.stringify(command));
  const isDirty = JSON.stringify(command) !== initialCommand;

  useEffect(() => {
    if (!isDirty) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  // Champs nécessaires au verdict de la sous-étape (miroir des « à compléter » du back).
  const missingRequired = useMemo(() => {
    const missing: string[] = [];
    if (parsedArea == null || parsedArea <= 0) missing.push("la surface totale");
    if (parsedGreenSpaceArea == null) missing.push("l'espace vert");
    if (above1500m == null) missing.push("l'altitude");
    if (above1500m === true && fireStationWithin3Km == null) missing.push("le poste de secours");
    if (!natureHousing && !natureOffices && !natureShops && !natureParking) missing.push("au moins une nature présente");
    return missing;
  }, [
    parsedArea,
    parsedGreenSpaceArea,
    above1500m,
    fireStationWithin3Km,
    natureHousing,
    natureOffices,
    natureShops,
    natureParking,
  ]);
  const canSubmit = missingRequired.length === 0 && !hasAreaError;

  useEffect(() => {
    onCanSubmitChange(canSubmit);
  }, [onCanSubmitChange, canSubmit]);

  const buildCommand = useCallback(
    (): UpdateCopropertyCommand | null => (canSubmit ? command : null),
    [canSubmit, command]
  );

  useEffect(() => {
    registerBuildCommand(buildCommand);
  }, [registerBuildCommand, buildCommand]);

  return (
    <>
      <CopropertySection
        title="Surfaces"
        titleAside={
          onEstimateTerrain && (
            <button
              type="button"
              disabled={isEstimating}
              onClick={() => applyEstimation(true)}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline disabled:opacity-50"
            >
              {isEstimating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {isEstimating ? "Estimation du terrain…" : "Recalculer espace vert et altitude"}
            </button>
          )
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <InputText label="Nom d'usage" name="name" value={name} onChange={(e) => setName(e.target.value)} />
          <InputWithUnit
            label="Surface totale"
            name="area"
            unit="m²"
            type="number"
            min={0}
            required
            labelTooltip="Confiancia propose des produits adaptés à toutes les surfaces, même les plus petites."
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
          <div className="flex flex-col gap-1">
            <InputWithUnit
              label="Espace vert"
              name="greenSpaceArea"
              unit="m²"
              type="number"
              min={0}
              required
              labelTooltip="Surface de la parcelle hors emprise des bâtiments. Au-delà de 100 000 m², la copropriété n'est pas assurable."
              value={greenSpaceArea}
              onChange={(e) => setGreenSpaceArea(e.target.value)}
            />
            {greenSpaceHint && <p className="text-xs text-gray-500">{greenSpaceHint}</p>}
          </div>
        </div>
        {estimation && estimation.warnings.length > 0 && !estimation.parcelArea && (
          <p className="text-xs text-gray-500">{estimation.warnings[0]}</p>
        )}
      </CopropertySection>

      <CopropertySection title="Caractéristiques">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Dropdown
            label="Standing de l'immeuble"
            name="buildingStanding"
            placeholder="Sélectionner"
            required
            items={BUILDING_STANDING_ITEMS}
            selectedItem={BUILDING_STANDING_ITEMS.find((item) => item.key === buildingStanding) ?? null}
            onSelect={(item) => setBuildingStanding(Number(item.key) as BuildingStanding)}
          />
        </div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <CompactBooleanRow
            label="Construite il y a plus de 10 ans (révolus) ?"
            value={builtMoreThan10}
            onChange={setBuiltMoreThan10}
          />
          <CompactBooleanRow
            label="Occupée à moins de 50 % ?"
            value={occupiedLess50}
            onChange={setOccupiedLess50}
          />
          <CompactBooleanRow
            label="Classée ou répertoriée sur la liste des bâtiments de la ville ?"
            value={listedOrRegistered}
            onChange={setListedOrRegistered}
          />
          <CompactBooleanRow
            label="À plus de 1 500 m d'altitude ?"
            value={above1500m}
            required
            hint={altitudeHint}
            onChange={setAbove1500m}
          />
          {above1500m === true && (
            <div className="sm:col-span-2">
              <CompactBooleanRow
                label="Un poste de secours pompiers est-il à moins de 3 km ou 30 mn ?"
                value={fireStationWithin3Km}
                required
                hint="Sans poste de secours à proximité, la copropriété n'est pas assurable."
                onChange={setFireStationWithin3Km}
              />
            </div>
          )}
        </div>

        {isVacant && (
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            Copropriété dite « vacante » : un produit « MRI inoccupé » sera proposé à la tarification.
          </p>
        )}
        {listedOrRegistered === true && (
          <p className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            Confiancia propose des solutions pour les immeubles classés ou répertoriés.
          </p>
        )}
      </CopropertySection>

      <CopropertySection
        title="Natures présentes"
        titleAside={<span className="text-sm text-gray-400">Cumul possible</span>}
      >
        <div className="flex flex-wrap gap-2">
          <NatureChip label="Habitation" checked={natureHousing} onChange={setNatureHousing} />
          <NatureChip label="Bureaux" checked={natureOffices} onChange={setNatureOffices} />
          <NatureChip label="Commerces" checked={natureShops} onChange={setNatureShops} />
          <NatureChip label="Parking commercial" checked={natureParking} onChange={setNatureParking} />
        </div>

        {(natureParking || natureShops || natureOffices) && (
          <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
            {natureParking && (
              <InputWithUnit
                label="Surface du parking commercial"
                name="commercialParkingArea"
                unit="m²"
                type="number"
                min={0}
                error={areaErrors.parking ? areaErrorHint : undefined}
                value={parkingArea}
                onChange={(e) => setParkingArea(e.target.value)}
              />
            )}
            {natureShops && (
              <>
                <InputWithUnit
                  label="Surface commerciale"
                  name="shopsArea"
                  unit="m²"
                  type="number"
                  min={0}
                  error={areaErrors.shops ? areaErrorHint : undefined}
                  value={shopsArea}
                  onChange={(e) => setShopsArea(e.target.value)}
                />
                <InputText
                  label="Activité de commerce"
                  name="shopType"
                  value={shopType}
                  onChange={(e) => setShopType(e.target.value)}
                />
              </>
            )}
            {natureOffices && (
              <InputWithUnit
                label="Surface de bureaux"
                name="officesArea"
                unit="m²"
                type="number"
                min={0}
                error={areaErrors.offices ? areaErrorHint : undefined}
                value={officesArea}
                onChange={(e) => setOfficesArea(e.target.value)}
              />
            )}
          </div>
        )}
      </CopropertySection>

      {verdict && verdict.status !== CopropertyEligibilityStatus.Eligible && verdict.reasons.length > 0 && (
        <EligibilityVerdictBanner verdict={verdict} />
      )}

      {/* État de la saisie : ce qui bloque, ou ce qui attend le bouton d'étape */}
      {hasAreaError ? (
        <p className="flex items-center gap-2 text-[13px] text-amber-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Chaque surface par nature doit rester inférieure à la surface totale.
        </p>
      ) : missingRequired.length > 0 ? (
        <p className="flex items-center gap-2 text-[13px] text-amber-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Renseignez {missingRequired.join(", ")} pour passer au questionnaire.
        </p>
      ) : (
        isDirty && (
          <p className="flex items-center gap-2 text-[13px] text-gray-500">
            <PencilLine className="h-4 w-4 shrink-0 text-gray-400" />
            Modifications non enregistrées · elles le seront en passant au questionnaire.
          </p>
        )
      )}
    </>
  );
};
